
import { format } from "date-fns";
import { Unlink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

interface LinkedProfileCardProps {
  profile: any;
  onUnlink: () => Promise<void>;
  showUnlinkDialog: boolean;
  setShowUnlinkDialog: (show: boolean) => void;
  isLoading: boolean;
}

export function LinkedProfileCard({ 
  profile, 
  onUnlink, 
  showUnlinkDialog, 
  setShowUnlinkDialog,
  isLoading 
}: LinkedProfileCardProps) {
  // Determine the badge variant based on subscription status
  const getBadgeVariant = (status?: string) => {
    if (!status) return "outline";
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'pending': return 'secondary';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="font-medium">Currently linked to:</p>
            <Badge variant="outline">{profile.role || 'user'}</Badge>
            {profile.subscription_status && (
              <Badge variant={getBadgeVariant(profile.subscription_status)}>
                {profile.subscription_status}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {profile.phone_number || 'No phone number'}
          </p>
          <p className="text-xs text-muted-foreground">
            Linked since: {format(new Date(profile.updated_at), 'MMM d, yyyy')}
          </p>
          {profile.subscription_status && (
            <p className="text-xs text-muted-foreground">
              Subscription status: {profile.subscription_status}
            </p>
          )}
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowUnlinkDialog(true)}
        >
          <Unlink className="h-4 w-4 mr-2" />
          Unlink Profile
        </Button>
      </div>

      <Dialog open={showUnlinkDialog} onOpenChange={setShowUnlinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Unlink Profile</DialogTitle>
          </DialogHeader>
          <div className="p-4 border rounded-md bg-muted/50">
            <p><strong>Phone:</strong> {profile.phone_number || 'None'}</p>
            <p><strong>Linked since:</strong> {format(new Date(profile.updated_at), 'MMM d, yyyy')}</p>
            <p><strong>Role:</strong> {profile.role || 'user'}</p>
            {profile.subscription_status && (
              <p><strong>Subscription:</strong> {profile.subscription_status}</p>
            )}
          </div>
          <DialogFooter>
            <AlertDialogCancel onClick={() => setShowUnlinkDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onUnlink}
              className="bg-red-600 hover:bg-red-700"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing
                </>
              ) : (
                'Unlink Profile'
              )}
            </AlertDialogAction>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
